import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import type { DoubtfulRow } from '@/pages/purchases/components/excelImport/excelImportMeta';
import { DoubtfulRowCard } from './DoubtfulRowCard';

type DoubtfulSectionProps = {
    rows: DoubtfulRow[];
    onPromote: (id: string) => void;
    onDismiss: (id: string) => void;
};

/** The "Doubtful imports" card: Excel rows the analyzer couldn't cleanly
 *  convert, each fixable via Promote (append as editable line) or Dismiss.
 *  Renders nothing once every row is handled. */
export const DoubtfulSection: React.FC<DoubtfulSectionProps> = ({
    rows,
    onPromote,
    onDismiss,
}) => {
    const { t } = useTranslation('pages');

    if (rows.length === 0) {
        return null;
    }

    return (
        <section {...getNodeTestIdProp('file-import-doubtful')}>
            <Card {...getNodeTestIdProp('file-import-doubtful-card')}>
                <Card.Header
                    {...getNodeTestIdProp('file-import-doubtful-header')}
                >
                    <Card.Title
                        {...getNodeTestIdProp('file-import-doubtful-title')}
                    >
                        {t('purchases.fileImport.doubtful.title')}
                    </Card.Title>
                    <Card.Description
                        {...getNodeTestIdProp('file-import-doubtful-subtitle')}
                    >
                        {t('purchases.fileImport.doubtful.subtitle', {
                            count: rows.length,
                        })}
                    </Card.Description>
                </Card.Header>
                <Card.Content
                    {...getNodeTestIdProp('file-import-doubtful-content')}
                >
                    <div className="flex flex-col gap-3">
                        {rows.map((row) => (
                            <DoubtfulRowCard
                                key={row.id}
                                onDismiss={onDismiss}
                                onPromote={onPromote}
                                row={row}
                            />
                        ))}
                    </div>
                </Card.Content>
            </Card>
        </section>
    );
};
